import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from "react";
import { DEFAULT_GITHUB_INSTANCE_SLUG, urlInstanceSlug } from "@/lib/named-org";
import { authHadSession } from "@/lib/authHadSession";
import { useDashboardInstanceSlug } from "@/hooks/useDashboardNavigation";
import { useWorkosAuthEnabled } from "@/lib/workos-auth";
import { BlackGitHubIcon, WhiteGitHubIcon } from "./GitHubIcon";
import { Button } from "@repo/blacksmith-ui/button";
import FadeIn from "./FadeIn";
import { AnimatedBlacksmithLogoOneline, BlacksmithLogomarkAnimationSVG } from "./BlacksmithLogoSVG";
import { usePostHog } from "posthog-js/react";
import { CORE_ANALYTICS_EVENTS } from "@/lib/posthog-constants";
import { useTheme } from "next-themes";
import { useDashboardSearchParams as useSearchParams } from "@/hooks/useDashboardNavigation";
import { withFirstTouchRdtCid } from "@/lib/appFirstTouchAttribution";
import { codesmithDesktopBridge, useCodesmithSurface } from "@/lib/codesmithSurface";
import { WorkOSLoginPage } from "./workos/WorkOSLoginPage";
import { EnterpriseInstanceLookup } from "./EnterpriseInstanceLookup";
export function LoginCard() {
  const $ = _c(20);
  const {
    workosAuthEnabled,
    isLoading: isFlagLoading
  } = useWorkosAuthEnabled();
  const [isLoading, setIsLoading] = useState(false);
  const posthog = usePostHog();
  const {
    resolvedTheme
  } = useTheme();
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams.get("embed");
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const surface = useCodesmithSurface(t0);
  const instanceSlug = useDashboardInstanceSlug();
  const instance = instanceSlug === DEFAULT_GITHUB_INSTANCE_SLUG ? null : instanceSlug;
  const loginPath = instance ? encodeURIComponent(instance) : workosAuthEnabled ? "workos" : "github";
  let t1;
  if ($[2] !== instance || $[3] !== loginPath || $[4] !== posthog || $[5] !== surface || $[6] !== workosAuthEnabled) {
    t1 = () => {
      setIsLoading(true);
      posthog?.capture(CORE_ANALYTICS_EVENTS.LOGIN_BUTTON_CLICK, {
        authentication_provider: instance ? "github" : workosAuthEnabled ? "workos" : "github"
      });
      const redirectUrl = encodeURIComponent(withFirstTouchRdtCid(window.location.href));
      const loginUrl = `${process.env.NEXT_PUBLIC_API_URL}/login/${loginPath}?redirect=${redirectUrl}`;
      if (surface === "desktop") {
        codesmithDesktopBridge()?.startLogin();
        setIsLoading(false);
        return;
      }
      if (surface === "extension") {
        window.open(loginUrl, "_blank");
        setIsLoading(false);
        return;
      }
      window.location.href = loginUrl;
    };
    $[2] = instance;
    $[3] = loginPath;
    $[4] = posthog;
    $[5] = surface;
    $[6] = workosAuthEnabled;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const handleLogin = t1;
  if (isLoading) {
    let t2;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="flex h-screen items-center justify-center"><div className="w-32 h-32"><BlacksmithLogomarkAnimationSVG /></div></div>;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    return t2;
  }
  let t2;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="w-48 mx-auto py-4 mb-0 text-neutral-900 dark:text-blacksmith-yellow"><AnimatedBlacksmithLogoOneline /></div>;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="-mt-4 flex flex-col text-neutral-800 dark:text-white"><span className="text-xl font-medium">Sign in</span><span className="text-sm text-neutral-500 dark:text-neutral-400">to continue to Blacksmith</span></div>;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== resolvedTheme) {
    t4 = <>{resolvedTheme === "dark" ? <BlackGitHubIcon /> : <WhiteGitHubIcon />}Continue with GitHub</>;
    $[11] = resolvedTheme;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== handleLogin || $[14] !== isFlagLoading || $[15] !== t4) {
    t5 = <Button disabled={isFlagLoading} onClick={handleLogin} className="flex gap-2" variant="default" data-test="github-login-button">{t4}</Button>;
    $[13] = handleLogin;
    $[14] = isFlagLoading;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="-mt-2 block text-sm text-neutral-500 dark:text-neutral-400">No account? We'll create one for you.</span>;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t5) {
    t7 = <div className="w-[360px] flex flex-col gap-6 text-center rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-8">{t2}{t3}{t5}{t6}</div>;
    $[18] = t5;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}
export default function SignedOut(t0) {
  const $ = _c(14);
  const {
    autoLogin: t1
  } = t0;
  const autoLogin = t1 === undefined ? false : t1;
  const searchParams = useSearchParams();
  let t2;
  if ($[0] !== searchParams) {
    t2 = searchParams.get("autoLogin");
    $[0] = searchParams;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const autoLoginParam = t2;
  const instanceSlug = useDashboardInstanceSlug();
  const instance = instanceSlug === DEFAULT_GITHUB_INSTANCE_SLUG ? null : instanceSlug;
  const {
    workosAuthEnabled,
    isLoading: isFlagLoading
  } = useWorkosAuthEnabled();
  let t3;
  if ($[2] !== searchParams) {
    t3 = searchParams.get("embed");
    $[2] = searchParams;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const surface = useCodesmithSurface(t3);
  const autoLoginDisabled = autoLoginParam === "false" || surface !== "web";
  let t4;
  if ($[4] !== autoLogin || $[5] !== searchParams) {
    t4 = autoLogin || searchParams.has("autoLogin");
    $[4] = autoLogin;
    $[5] = searchParams;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const autoLoginRequested = t4;
  const shouldRedirectToLogin = !isFlagLoading && !autoLoginDisabled && autoLoginRequested;
  let t5;
  let t6;
  if ($[7] !== shouldRedirectToLogin) {
    t5 = () => {
      if (!shouldRedirectToLogin) {
        return;
      }
      const slug = urlInstanceSlug(undefined, authHadSession.readLoginInstance());
      const redirectUrl = encodeURIComponent(withFirstTouchRdtCid(window.location.href));
      window.location.replace(`${process.env.NEXT_PUBLIC_API_URL}/login/${encodeURIComponent(slug)}?redirect=${redirectUrl}`);
    };
    t6 = [shouldRedirectToLogin];
    $[7] = shouldRedirectToLogin;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  useEffect(t5, t6);
  if (shouldRedirectToLogin || isFlagLoading) {
    let t7;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="flex h-screen items-center justify-center bg-app-background"><div className="w-32 h-32"><BlacksmithLogomarkAnimationSVG /></div></div>;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    return t7;
  }
  if (workosAuthEnabled && !instance) {
    let t7;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <WorkOSLoginPage />;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    return t7;
  }
  let t7;
  if ($[12] !== instance) {
    t7 = <div className="flex flex-col items-center justify-center min-h-screen bg-app-background"><FadeIn>{instance === null ? <EnterpriseInstanceLookup><LoginCard /></EnterpriseInstanceLookup> : <LoginCard />}</FadeIn></div>;
    $[12] = instance;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}